import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid';
import { Paper, Typography, Box, CircularProgress, Autocomplete, TextField, Button } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import QuartileDistributionChart from '../components/Dashboard/QuartileDistributionChart';
import CompanyAdvancedMetrics from '../components/Dashboard/CompanyAdvancedMetrics';
import CompanyRawMetrics from '../components/Dashboard/CompanyRawMetrics';
import CompanyProfileCard from '../components/SearchCompany/CompanyProfileCard';
import ReportingTimeliness from '../components/SearchCompany/ReportingTimeliness';
import CompanyTrendChart from '../components/SearchCompany/CompanyTrendChart';
import { useCountry } from '../context/CountryContext';
import { API_BASE_URL } from '../config';
import { hasQuartileDataForYear } from '../utils/quartiles';
import { fetchJsonWithCache, getCachedJson } from '../utils/apiCache';

const REPORTING_YEARS = [2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024];
const MIN_SEARCH_CHARS = 2;
const SEARCH_PAGE_SIZE = 20;

const normalizeSearchPayload = (payload: any): { items: any[]; nextCursor: string | null } => {
  if (Array.isArray(payload)) {
    return { items: payload, nextCursor: null };
  }
  return {
    items: payload?.items || [],
    nextCursor: payload?.next_cursor || null,
  };
};

const SearchCompany: React.FC = () => {
  const { country } = useCountry();
  const location = useLocation();
  const { companyId } = useParams<{ companyId?: string }>();
  const navigate = useNavigate();
  const [companyList, setCompanyList] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [detailsLoading, setDetailsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [urlCompanyQuery, setUrlCompanyQuery] = useState<string | null>(null);
  const [urlCompanyId, setUrlCompanyId] = useState<string | null>(null);
  const [selectedQuartileYear, setSelectedQuartileYear] = useState<number | null>(null);

  useEffect(() => {
    setSelectedCompany(null);
    setCompanyList([]);
    setNextCursor(null);
    setSearchQuery('');
    setSelectedQuartileYear(null);
  }, [country, navigate]);

  useEffect(() => {
    if (!companyId) return;
    setSelectedCompany(null);
    setUrlCompanyId(companyId);
    setUrlCompanyQuery(null);
  }, [companyId]);

  useEffect(() => {
    if (companyId) return;
    const params = new URLSearchParams(location.search);
    const companyParam = params.get('company');
    const companyIdParam = params.get('companyId');
    if (companyParam) {
      setSelectedCompany(null);
      setSearchQuery(companyParam);
      setUrlCompanyQuery(companyParam);
    }
    if (companyIdParam) {
      setSelectedCompany(null);
      setUrlCompanyId(companyIdParam);
    }
  }, [location.search, companyId]);

  useEffect(() => {
    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery.length < MIN_SEARCH_CHARS) {
      setCompanyList([]);
      setNextCursor(null);
      setLoading(false);
      setLoadingMore(false);
      return;
    }

    const normalizedQuery = trimmedQuery.toLowerCase();

    const fetchFirstPage = async () => {
      setLoading(true);
      setError(null);
      setNextCursor(null);
      try {
        const response = await fetch(
          `${API_BASE_URL}/companies/search?country=${country}&q=${encodeURIComponent(normalizedQuery)}&limit=${SEARCH_PAGE_SIZE}&paginated=1`
        );
        if (!response.ok) throw new Error('Failed to fetch company list');
        const payload = await response.json();
        const normalizedPayload = normalizeSearchPayload(payload);
        setCompanyList(normalizedPayload.items);
        setNextCursor(normalizedPayload.nextCursor);
      } catch (err: any) {
        setError(err.message);
        setCompanyList([]);
        setNextCursor(null);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchFirstPage, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, country]);

  const handleLoadMore = useCallback(async () => {
    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery.length < MIN_SEARCH_CHARS || !nextCursor || loadingMore) {
      return;
    }

    setLoadingMore(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_BASE_URL}/companies/search?country=${country}&q=${encodeURIComponent(trimmedQuery.toLowerCase())}&limit=${SEARCH_PAGE_SIZE}&cursor=${encodeURIComponent(nextCursor)}&paginated=1`
      );
      if (!response.ok) throw new Error('Failed to fetch additional companies');
      const payload = await response.json();
      const normalizedPayload = normalizeSearchPayload(payload);
      setCompanyList((prev) => [...prev, ...normalizedPayload.items]);
      setNextCursor(normalizedPayload.nextCursor);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoadingMore(false);
    }
  }, [country, searchQuery, nextCursor, loadingMore]);

  const fetchCompanyDetailsById = useCallback(async (id: string) => {
    const cacheKey = `company-details:${country}:${id}`;
    const url = `${API_BASE_URL}/companies/${id}/details?country=${country}`;
    const cachedDetails = getCachedJson(cacheKey);
    if (cachedDetails) {
      setSelectedCompany(cachedDetails);
      if (cachedDetails?.CleanCompanyName) {
        setSearchQuery(cachedDetails.CleanCompanyName);
      }
      return;
    }

    setDetailsLoading(true);
    try {
      const details = await fetchJsonWithCache(cacheKey, url);
      setSelectedCompany(details);
      if (details?.CleanCompanyName) {
        setSearchQuery(details.CleanCompanyName);
        navigate(`/company/${id}`, { replace: true });
      }
    } catch (err: any) {
      setError(err.message);
      setSelectedCompany(null);
    } finally {
      setDetailsLoading(false);
    }
  }, [country, navigate]);

  const handleCompanySelect = useCallback(async (company: any) => {
    if (!company) {
      setSelectedCompany(null);
      return;
    }

    setDetailsLoading(true);
    try {
      const cacheKey = `company-details:${country}:${company.id}`;
      const url = `${API_BASE_URL}/companies/${company.id}/details?country=${country}`;
      const details = await fetchJsonWithCache(cacheKey, url);
      setSelectedCompany(details);
      if (details?.CleanCompanyName) {
        setSearchQuery(details.CleanCompanyName);
        navigate(`/company/${company.id}`, { replace: true });
      }
    } catch (err: any) {
      setError(err.message);
      setSelectedCompany(null);
    } finally {
      setDetailsLoading(false);
    }
  }, [country, navigate]);

  useEffect(() => {
    if (!urlCompanyId) return;
    setUrlCompanyQuery(null);
    fetchCompanyDetailsById(urlCompanyId);
    setUrlCompanyId(null);
  }, [urlCompanyId, fetchCompanyDetailsById]);

  useEffect(() => {
    if (!urlCompanyQuery || urlCompanyId || companyList.length === 0) return;

    const normalize = (value: string) => value.trim().toLowerCase();
    const exactMatch = companyList.find(
      (company) => normalize(company.CleanCompanyName || '') === normalize(urlCompanyQuery)
    );
    const partialMatch = companyList.find(
      (company) => normalize(company.CleanCompanyName || '').includes(normalize(urlCompanyQuery))
    );

    if (exactMatch) {
      handleCompanySelect(exactMatch);
      setUrlCompanyQuery(null);
    } else if (partialMatch) {
      handleCompanySelect(partialMatch);
      setUrlCompanyQuery(null);
    }
  }, [companyList, urlCompanyQuery, urlCompanyId, handleCompanySelect]);

  useEffect(() => {
    if (!selectedCompany) {
      setSelectedQuartileYear(null);
      return;
    }

    const yearsDesc = [...REPORTING_YEARS].sort((a, b) => b - a);
    const yearsWithTimeliness = yearsDesc.filter((year) => {
      const submittedAfterDeadline = selectedCompany[`${year}_SubmittedAfterTheDeadline`];
      return submittedAfterDeadline !== undefined && submittedAfterDeadline !== null;
    });
    const yearWithTimelinessAndQuartiles = yearsWithTimeliness.find((year) => hasQuartileDataForYear(selectedCompany, year));

    if (yearWithTimelinessAndQuartiles) {
      setSelectedQuartileYear(yearWithTimelinessAndQuartiles);
      return;
    }
    if (yearsWithTimeliness.length > 0) {
      setSelectedQuartileYear(yearsWithTimeliness[0]);
      return;
    }

    const latestQuartileYear = yearsDesc.find((year) => hasQuartileDataForYear(selectedCompany, year));
    setSelectedQuartileYear(latestQuartileYear ?? null);
  }, [selectedCompany]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper elevation={3} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>Search Company</Typography>
          <Autocomplete
            options={companyList}
            filterOptions={(options) => options}
            getOptionLabel={(option) => option.CleanCompanyName || ''}
            onChange={(event, newValue) => handleCompanySelect(newValue)}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === 'input' || reason === 'clear') {
                setSearchQuery(newInputValue);
              }
              if (reason === 'input') {
                setUrlCompanyQuery(null);
              }
            }}
            inputValue={searchQuery}
            loading={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search for any company..."
                variant="outlined"
                helperText="Start typing to search (minimum 2 characters)"
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return;
                  const trimmed = searchQuery.trim();
                  if (trimmed.length < MIN_SEARCH_CHARS) return;
                  const normalize = (value: string) => value.trim().toLowerCase();
                  const exactMatch = companyList.find(
                    (company) => normalize(company.CleanCompanyName || '') === normalize(trimmed)
                  );
                  if (exactMatch) {
                    event.preventDefault();
                    handleCompanySelect(exactMatch);
                  }
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                {option.CleanCompanyName}
              </Box>
            )}
          />
          {nextCursor && searchQuery.trim().length >= MIN_SEARCH_CHARS && (
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
              <Button
                onClick={handleLoadMore}
                disabled={loadingMore}
                size="small"
                variant="text"
              >
                {loadingMore ? 'Loading more...' : 'Load more results'}
              </Button>
            </Box>
          )}
        </Paper>
      </Grid>

      {error && <Grid item xs={12}><Typography color="error" align="center">{error}</Typography></Grid>}

      {detailsLoading && (
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        </Grid>
      )}

      {selectedCompany && !detailsLoading && (
        <Grid item xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <CompanyProfileCard company={selectedCompany} />
                </Grid>
                <Grid item xs={12}>
                  <ReportingTimeliness
                    company={selectedCompany}
                    selectedYear={selectedQuartileYear}
                    onYearSelect={(year) => setSelectedQuartileYear(year)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CompanyRawMetrics company={selectedCompany} selectedYear={selectedQuartileYear} />
                </Grid>
                <Grid item xs={12}>
                  <CompanyTrendChart company={selectedCompany} />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <QuartileDistributionChart company={selectedCompany} selectedYear={selectedQuartileYear} />
                </Grid>
                <Grid item xs={12}>
                  <CompanyAdvancedMetrics company={selectedCompany} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default SearchCompany;
